import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Building2,
  Users,
  FileText,
  CreditCard,
  FileDown,
  ChevronLeft,
  AlertTriangle,
  Activity,
  Armchair,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useIomiDashboard } from '../../hooks/useIomiDashboard';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { api } from '../../lib/api';

// ── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_PDF_LIMIT = 500;

const SUB_COLORS: Record<string, string> = {
  active: '#059669',
  trialing: '#820263',
  past_due: '#F75C03',
  canceled: '#D90368',
  unpaid: '#D90368',
  none: '#9E8FA3',
  incomplete: '#9E8FA3',
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#9E8FA3',
  sent_to_client: '#820263',
  closed: '#059669',
  cancelled: '#D90368',
};

const SUB_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past Due',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  none: 'None',
  incomplete: 'Incomplete',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Custom Tooltip ──────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: { value: number; name: string; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone rounded-lg shadow-brand-md px-3 py-2 text-xs">
      {label && <p className="font-semibold text-midnight mb-1">{label}</p>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-slate">{entry.name}:</span>
          <span className="font-mono font-medium text-midnight">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function IomiDashboardPage() {
  const navigate = useNavigate();
  const { currentUser } = useUserProfile();

  // Demo filter state
  const [excludeDemo, setExcludeDemo] = useState(true);
  const { data, loading, error, refetch } = useIomiDashboard(excludeDemo);

  // PDF limit editing state
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitDraft, setLimitDraft] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);

  // Access guard
  if (!currentUser?.isPlatformAdmin && IS_FIREBASE_CONFIGURED) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <AlertTriangle size={48} className="text-slate mb-4" />
        <h2 className="font-display text-h3 text-midnight mb-2">Access Denied</h2>
        <p className="text-small text-slate">
          This dashboard is only available to IOMI administrators.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <Activity size={48} className="text-rose mb-4" />
        <h2 className="font-display text-h3 text-midnight mb-2">Something went wrong</h2>
        <p className="text-small text-slate mb-4">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const {
    kpis,
    orgGrowth,
    subscriptionBreakdown,
    quotesByOrg,
    pdfsByMonth,
    quoteStatusPlatform,
    orgList,
    usersByRole,
    pdfMonthlyLimit,
  } = data;

  const pdfLimit = pdfMonthlyLimit ?? DEFAULT_PDF_LIMIT;

  const chartOrgGrowth = orgGrowth.map(d => ({ ...d, label: formatMonth(d.month) }));
  const chartPdfs = pdfsByMonth.map(d => ({ ...d, label: formatMonth(d.month) }));

  // Latest month PDF count for limit warning
  const latestMonthPdfs = pdfsByMonth.length > 0
    ? pdfsByMonth[pdfsByMonth.length - 1].count
    : 0;
  const pdfUsagePct = (latestMonthPdfs / pdfLimit) * 100;
  const pdfWarning = pdfUsagePct >= 80;

  function startEditingLimit() {
    setLimitDraft(String(pdfLimit));
    setEditingLimit(true);
  }

  async function saveLimit() {
    const num = parseInt(limitDraft, 10);
    if (isNaN(num) || num < 1) return;
    setSavingLimit(true);
    try {
      await api.patch('/admin/app-config', { pdfMonthlyLimit: num });
      setEditingLimit(false);
      refetch();
    } catch {
      // Silently fail — user will see old value
    } finally {
      setSavingLimit(false);
    }
  }

  return (
    <div className="px-4 pt-4 pb-8 md:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-h1 text-midnight">Platform Dashboard</h1>
          <p className="text-small text-slate mt-0.5">
            Platform-wide health, usage, and growth metrics.
          </p>
        </div>
      </div>

      {/* Demo org filter toggle */}
      <div className="flex items-center gap-3 mb-5">
        <button
          type="button"
          role="switch"
          aria-checked={excludeDemo}
          onClick={() => setExcludeDemo(v => !v)}
          className={`relative shrink-0 w-10 h-[22px] rounded-full transition-colors duration-200
            ${excludeDemo ? 'bg-plum' : 'bg-stone'}`}
        >
          <span
            className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-200
              ${excludeDemo ? 'translate-x-[18px]' : 'translate-x-0'}`}
          />
        </button>
        <span className="text-[13px] text-midnight font-medium">
          Exclude demo organizations
        </span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard label="Organizations" value={String(kpis.totalOrgs)} icon={<Building2 size={18} />} color="plum" />
        <KpiCard label="Active Subs" value={String(kpis.activeSubscriptions)} icon={<CreditCard size={18} />} color="emerald" />
        <KpiCard label="Total Users" value={String(kpis.totalUsers)} icon={<Users size={18} />} color="plum" />
        <KpiCard label="Total Licenses" value={String(kpis.totalSubscriptionQuantity)} icon={<Armchair size={18} />} color="plum" />
        <KpiCard label="Total Quotes" value={String(kpis.totalQuotes)} icon={<FileText size={18} />} color="plum" />
        <KpiCard label="PDFs Generated" value={String(kpis.totalPdfsGenerated)} icon={<FileDown size={18} />} color="emerald" />
      </div>

      {/* PDF Usage Alert */}
      {pdfWarning && (
        <div className={`mb-5 rounded-xl border px-4 py-3 flex items-start gap-3
          ${pdfUsagePct >= 100
            ? 'bg-rose/5 border-rose/40 text-rose'
            : 'bg-amber-50 border-amber-300 text-amber-800'}`}
        >
          <AlertTriangle size={20} className="shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">
              {pdfUsagePct >= 100
                ? 'CraftMyPDF monthly limit reached!'
                : 'Approaching CraftMyPDF monthly limit'}
            </p>
            <p className="text-xs mt-0.5 opacity-80">
              {latestMonthPdfs} of {pdfLimit} PDFs used this month ({pdfUsagePct.toFixed(0)}%).
              {pdfUsagePct >= 100 ? ' Consider upgrading your CraftMyPDF tier.' : ''}
            </p>
          </div>
        </div>
      )}

      {/* PDF Generation by Month — featured chart */}
      <ChartCard title="PDF Generation by Month" className="mb-6">
        {chartPdfs.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartPdfs} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={{ stroke: '#E8E3EA' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} />
                  )}
                />
                <Bar dataKey="count" name="PDFs" fill="#820263" radius={[4, 4, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
            {/* Usage bar */}
            <div className="mt-3 pt-3 border-t border-stone/60">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-midnight">
                  Current month usage
                </span>
                <div className="flex items-center gap-1.5">
                  {editingLimit ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-mono text-midnight">{latestMonthPdfs} /</span>
                      <input
                        type="number"
                        min="1"
                        value={limitDraft}
                        onChange={e => setLimitDraft(e.target.value)}
                        className="w-16 h-6 px-1.5 rounded border border-plum/40 text-xs font-mono text-midnight
                                   focus:outline-none focus:ring-1 focus:ring-plum/30"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveLimit();
                          if (e.key === 'Escape') setEditingLimit(false);
                        }}
                      />
                      <button
                        type="button"
                        onClick={saveLimit}
                        disabled={savingLimit}
                        className="w-5 h-5 rounded flex items-center justify-center text-emerald-600 hover:bg-emerald-50"
                      >
                        <Check size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingLimit(false)}
                        className="w-5 h-5 rounded flex items-center justify-center text-slate hover:bg-mist"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={`text-xs font-mono font-bold ${pdfWarning ? 'text-rose' : 'text-midnight'}`}>
                        {latestMonthPdfs} / {pdfLimit}
                      </span>
                      <button
                        type="button"
                        onClick={startEditingLimit}
                        className="w-5 h-5 rounded flex items-center justify-center text-slate hover:text-plum hover:bg-plum/5
                                   transition-colors"
                        aria-label="Edit monthly PDF limit"
                      >
                        <Pencil size={11} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="h-2.5 bg-mist rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${
                    pdfUsagePct >= 100 ? 'bg-rose' : pdfUsagePct >= 80 ? 'bg-amber-400' : 'bg-plum'
                  }`}
                  style={{ width: `${Math.min(pdfUsagePct, 100)}%` }}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-slate mb-3">No PDF data yet.</p>
            {/* Still show the limit editor even with no data */}
            <div className="flex items-center justify-center gap-1.5">
              <span className="text-xs text-slate">Monthly limit:</span>
              {editingLimit ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="1"
                    value={limitDraft}
                    onChange={e => setLimitDraft(e.target.value)}
                    className="w-16 h-6 px-1.5 rounded border border-plum/40 text-xs font-mono text-midnight
                               focus:outline-none focus:ring-1 focus:ring-plum/30"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveLimit();
                      if (e.key === 'Escape') setEditingLimit(false);
                    }}
                  />
                  <button
                    type="button"
                    onClick={saveLimit}
                    disabled={savingLimit}
                    className="w-5 h-5 rounded flex items-center justify-center text-emerald-600 hover:bg-emerald-50"
                  >
                    <Check size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingLimit(false)}
                    className="w-5 h-5 rounded flex items-center justify-center text-slate hover:bg-mist"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-xs font-mono font-bold text-midnight">{pdfLimit}</span>
                  <button
                    type="button"
                    onClick={startEditingLimit}
                    className="w-5 h-5 rounded flex items-center justify-center text-slate hover:text-plum hover:bg-plum/5"
                    aria-label="Edit monthly PDF limit"
                  >
                    <Pencil size={11} />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </ChartCard>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Org Growth Over Time */}
        {chartOrgGrowth.length > 0 && (
          <ChartCard title="Organization Growth">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartOrgGrowth} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradOrgGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#820263" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#820263" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={{ stroke: '#E8E3EA' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="New Orgs"
                  stroke="#820263"
                  strokeWidth={2.5}
                  fill="url(#gradOrgGrowth)"
                  dot={{ r: 3, fill: '#820263', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#820263', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Subscription Breakdown — Donut */}
        {subscriptionBreakdown.length > 0 && (
          <ChartCard title="Subscription Status">
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={subscriptionBreakdown}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {subscriptionBreakdown.map(entry => (
                      <Cell
                        key={entry.status}
                        fill={SUB_COLORS[entry.status] ?? '#9E8FA3'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => (
                      <ChartTooltip active={active} payload={payload as never} />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-1">
                {subscriptionBreakdown.map(entry => (
                  <div key={entry.status} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: SUB_COLORS[entry.status] ?? '#9E8FA3' }}
                    />
                    <span className="text-slate">{entry.label}</span>
                    <span className="font-mono font-semibold text-midnight">{entry.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>
        )}

        {/* Quote Status Platform-wide */}
        {quoteStatusPlatform.length > 0 && (
          <ChartCard title="Quote Status (All Orgs)">
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={quoteStatusPlatform}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {quoteStatusPlatform.map(entry => (
                      <Cell
                        key={entry.status}
                        fill={STATUS_COLORS[entry.status] ?? '#9E8FA3'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => (
                      <ChartTooltip active={active} payload={payload as never} />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-1">
                {quoteStatusPlatform.map(entry => (
                  <div key={entry.status} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: STATUS_COLORS[entry.status] ?? '#9E8FA3' }}
                    />
                    <span className="text-slate">{entry.label}</span>
                    <span className="font-mono font-semibold text-midnight">{entry.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>
        )}

        {/* Quotes by Org */}
        {quotesByOrg.length > 0 && (
          <ChartCard title="Quotes by Organization">
            <ResponsiveContainer width="100%" height={Math.max(160, quotesByOrg.length * 44)}>
              <BarChart
                data={quotesByOrg}
                layout="vertical"
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="orgName"
                  tick={{ fontSize: 12, fill: 'var(--color-text-primary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={120}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} />
                  )}
                />
                <Bar dataKey="count" name="Quotes" fill="#820263" radius={[0, 6, 6, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Users by Role */}
      {usersByRole.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {usersByRole.map(r => (
            <div key={r.role} className="bg-white border border-stone rounded-lg px-3 py-2.5 text-center">
              <span className="font-mono text-lg font-bold text-midnight">{r.count}</span>
              <p className="text-[11px] text-slate font-medium mt-0.5 capitalize">{r.role}s</p>
            </div>
          ))}
        </div>
      )}

      {/* Organizations Table */}
      {orgList.length > 0 && (
        <ChartCard title="Organizations Overview">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <table className="w-full text-left text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-stone text-xs text-slate uppercase tracking-wider">
                  <th className="pb-2 font-semibold">Organization</th>
                  <th className="pb-2 font-semibold text-center">Users</th>
                  <th className="pb-2 font-semibold text-center">Quotes</th>
                  <th className="pb-2 font-semibold text-center">Subscription</th>
                  <th className="pb-2 font-semibold text-right">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone/60">
                {orgList.map(org => (
                  <tr
                    key={org.id}
                    className={`hover:bg-mist/50 cursor-pointer transition-colors
                      ${org.isDemo && excludeDemo ? 'opacity-40' : ''}`}
                    onClick={() => navigate(`/admin/platform-tools/organizations/${org.id}`)}
                  >
                    <td className="py-2.5 font-medium text-midnight">
                      <span className="flex items-center gap-1.5">
                        {org.name}
                        {org.isDemo && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold
                                         uppercase tracking-wide bg-amber-100 text-amber-700 leading-none">
                            Demo
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 text-center font-mono text-slate">{org.userCount}</td>
                    <td className="py-2.5 text-center font-mono text-slate">{org.quoteCount}</td>
                    <td className="py-2.5 text-center">
                      <span
                        className="inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${SUB_COLORS[org.subscriptionStatus] ?? '#9E8FA3'}18`,
                          color: SUB_COLORS[org.subscriptionStatus] ?? '#9E8FA3',
                        }}
                      >
                        {SUB_STATUS_LABELS[org.subscriptionStatus] ?? org.subscriptionStatus}
                      </span>
                    </td>
                    <td className="py-2.5 text-right text-xs text-slate">{formatDate(org.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function KpiCard({ label, value, icon, color }: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: 'plum' | 'emerald';
}) {
  const bg = color === 'plum' ? 'bg-plum/8' : 'bg-emerald-50';
  const iconColor = color === 'plum' ? 'text-plum' : 'text-emerald-600';

  return (
    <div className="bg-white border border-stone rounded-xl p-3.5 shadow-brand-sm
                    hover:shadow-brand-md hover:border-plum/30 transition-all">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center ${iconColor}`}>
          {icon}
        </div>
      </div>
      <p className="font-mono text-xl font-bold text-midnight leading-tight">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate mt-1">{label}</p>
    </div>
  );
}

function ChartCard({ title, children, className = '' }: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-white border border-stone rounded-xl p-4 shadow-brand-sm ${className}`}>
      <h3 className="font-display text-sm font-semibold text-midnight mb-3">{title}</h3>
      {children}
    </div>
  );
}
